import { instance } from './instance'

export const customerService = {
  // Zentrale axios-Instanz
  axiosInstance: instance,

  /**
   * Modernes/Erweitertes Menü mit Produkten abrufen
   * @returns {Promise<Object>} - Menü mit Kategorien und Produkten
   */
  async getModernMenu() {
    console.log('[customerService] Rufe modernes Menü ab');
    
    try {
      const response = await this.axiosInstance.get('/categories/with-products');
      console.log('[customerService] Modernes Menü erfolgreich abgerufen');
      return response;
    } catch (error) {
      console.error('[customerService] Fehler beim Abrufen des modernen Menüs:', error);
      
      // Falls der moderne Endpunkt nicht verfügbar ist, versuche den Standard-Menü-Endpunkt
      if (error.response && error.response.status === 404) {
        console.log('[customerService] Moderner Menü-Endpunkt nicht gefunden. Versuche Standard-Menü.');
        return this.getMenu();
      }
      
      throw error;
    }
  },
  
  /**
   * Produktdetails abrufen
   * @param {number} productId - ID des Produkts
   * @returns {Promise<Object>} - Produktdetails
   */
  async getProductDetails(productId) {
    console.log(`[customerService] Rufe Details für Produkt #${productId} ab`);
    
    try {
      const response = await this.axiosInstance.get(`/products/${productId}`);
      console.log(`[customerService] Details für Produkt #${productId} erfolgreich abgerufen`);
      return response;
    } catch (error) {
      console.error(`[customerService] Fehler beim Abrufen der Details für Produkt #${productId}:`, error);
      throw error;
    }
  },
  
  /**
   * Eine Session initialisieren
   * @param {Object} sessionData - Initialisierungsdaten für die Session
   * @returns {Promise<Object>} - Initialisierte Session
   */
  async initSession(sessionData) {
    console.log('[customerService] Initialisiere Session mit Daten:', sessionData);
    
    // Stelle sicher, dass wir die vom Backend erwarteten Felder senden
    // Wichtig: Der uniqueIdentifier wird hier hinzugefügt, um sicherzustellen, dass 
    // jede Session einzigartig ist, selbst wenn mehrere Kunden den gleichen QR-Code scannen
    const uniqueIdentifier = `${sessionData.tableId}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    console.log(`[customerService] Generierter Unique Identifier: ${uniqueIdentifier}`);
    
    try {
      const body = {
        tableId: sessionData.tableId,
        customerName: sessionData.customerName || '',
        customerEmail: sessionData.customerEmail || null,
        orderType: sessionData.orderType || 'eat-in',
        guestCount: sessionData.guestCount || 1,
        uniqueIdentifier: uniqueIdentifier
      };
      if (sessionData.entryToken) {
        body.entryToken = sessionData.entryToken;
      }
      const response = await this.axiosInstance.post('/ordersessions', body);
      
      console.log('[customerService] Session erfolgreich initialisiert, Session-ID:', response.data.sessionId);
      return response;
    } catch (error) {
      console.error('[customerService] Fehler beim Initialisieren der Session:', error);
      
      if (error.response) {
        console.error(`[customerService] Server antwortet mit Status ${error.response.status}:`, error.response.data);
      } else if (error.request) {
        console.error('[customerService] Keine Antwort vom Server erhalten - Netzwerkfehler');
      }
      
      throw error;
    }
  },
  
  /**
   * Bestellung aufgeben
   * @param {Object} orderData - Bestelldaten
   * @returns {Promise<Object>} - Erstellte Bestellung
   */
  async createOrder(orderData) {
    console.log('[customerService] Erstelle Bestellung mit Daten:', orderData);
    
    try {
      const response = await this.axiosInstance.post('/orders', orderData);
      console.log('[customerService] Bestellung erfolgreich erstellt, Bestellnummer:', response.data.orderNumber);
      return response;
    } catch (error) {
      console.error('[customerService] Fehler beim Erstellen der Bestellung:', error);
      
      // Verbesserte Fehlerdiagnose
      if (error.response) {
        console.error(`[customerService] Server antwortet mit Status ${error.response.status}:`, error.response.data);
        
        // Gib spezifischere Fehlermeldungen zurück, falls vorhanden
        if (error.response.data && error.response.data.message) {
          throw new Error(`Bestellfehler: ${error.response.data.message}`);
        }
      }
      
      throw error;
    }
  },
  
  /**
   * Wandelt den lokalen Warenkorb in das richtige Format für die API um
   * @param {Array} cartItems - Warenkorbpositionen
   * @returns {Object} - Formatierte Bestelldaten für die API
   */
  prepareCartItemsForOrder(cartItems) {
    console.log('[customerService] Bereite Warenkorb für Bestellung vor, Anzahl Positionen:', cartItems.length);
    
    const items = cartItems.map(item => {
      // Extrahiere die IDs der ausgewählten Extras
      const extraOptionIds = item.extras && item.extras.length > 0 
        ? item.extras.flatMap(extra => {
            // Berücksichtige Quantity für jedes Extra
            const extraQuantity = extra.quantity || 1;
            // Wiederhole die Extra-ID entsprechend der Menge
            return Array(extraQuantity).fill(extra.id);
          })
        : [];
        
      return {
        productId: item.id,
        quantity: item.quantity,
        specialInstructions: item.specialInstructions || '',
        extraOptionIds: extraOptionIds
      };
    });
    
    console.log('[customerService] Warenkorb für Bestellung vorbereitet');
    return { items };
  },

  /**
   * Bestelldetails abrufen
   * @param {number} orderId - ID der Bestellung
   * @returns {Promise<Object>} - Bestelldetails
   */
  async getOrderById(orderId) {
    console.log(`[customerService] Rufe Details für Bestellung #${orderId} ab`);
    
    try {
      const response = await this.axiosInstance.get(`/orders/${orderId}`);
      console.log(`[customerService] Details für Bestellung #${orderId} erfolgreich abgerufen`);
      return response;
    } catch (error) {
      console.error(`[customerService] Fehler beim Abrufen der Details für Bestellung #${orderId}:`, error);
      throw error;
    }
  },
  
  /**
   * Bestellstatus abrufen
   * @param {string} sessionId - ID der Session
   * @returns {Promise<Object>} - Bestellstatus
   */
  async getOrderStatus(sessionId) {
    console.log(`[customerService] Rufe Bestellstatus für Session ${sessionId} ab`);
    
    try {
      const response = await this.axiosInstance.get(`/orders/session/${sessionId}/status`);
      console.log(`[customerService] Bestellstatus für Session ${sessionId} erfolgreich abgerufen`);
      return response;
    } catch (error) {
      console.error(`[customerService] Fehler beim Abrufen des Bestellstatus für Session ${sessionId}:`, error);
      throw error;
    }
  },
  
  /**
   * Warenkorb für eine Session abrufen
   * @param {string} sessionId - ID der Session
   * @returns {Promise<Object>} - Warenkorb
   */
  async getCart(sessionId) {
    console.log(`[customerService] Rufe Warenkorb für Session ${sessionId} ab`);
    
    try {
      const response = await this.axiosInstance.get(`/orders/session/${sessionId}/cart`);
      console.log(`[customerService] Warenkorb für Session ${sessionId} erfolgreich abgerufen`);
      return response;
    } catch (error) {
      console.error(`[customerService] Fehler beim Abrufen des Warenkorbs für Session ${sessionId}:`, error);
      
      // Bei 404 einen leeren Warenkorb zurückgeben
      if (error.response && error.response.status === 404) {
        console.log(`[customerService] Kein Warenkorb für Session ${sessionId} gefunden, gebe leeren Warenkorb zurück`);
        return { data: { items: [] } };
      }
      
      throw error;
    }
  },
  
  /**
   * Öffentliche Restaurant-Einstellungen (Take-away, Dine-in, …)
   */
  async getRestaurantSettings() {
    return this.axiosInstance.get('/RestaurantSettings')
  },

  /**
   * Tischinformationen abrufen
   * @param {number} tableId - ID des Tisches
   * @returns {Promise<Object>} - Tischinformationen
   */
  async getTableInfo(tableId) {
    console.log(`[customerService] Rufe Informationen für Tisch #${tableId} ab`);
    
    try {
      const response = await this.axiosInstance.get(`/tables/${tableId}`);
      console.log(`[customerService] Informationen für Tisch #${tableId} erfolgreich abgerufen`);
      return response;
    } catch (error) {
      console.error(`[customerService] Fehler beim Abrufen der Informationen für Tisch #${tableId}:`, error);
      throw error;
    }
  },
  
  /**
   * Menü abrufen
   * @returns {Promise<Object>} - Menü mit Kategorien
   */
  async getMenu() {
    console.log('[customerService] Rufe Menü ab');
    
    try {
      const response = await this.axiosInstance.get('/categories');
      console.log('[customerService] Menü erfolgreich abgerufen');
      return response;
    } catch (error) {
      console.error('[customerService] Fehler beim Abrufen des Menüs:', error);
      throw error;
    }
  },

  /**
   * Kellner rufen
   * @param {string} sessionId - ID der Session
   * @returns {Promise<Object>} - Antwort vom Server
   */
  async callWaiter(sessionId) {
    console.log(`[customerService] Rufe Kellner für Session ${sessionId}`);
    
    try {
      const response = await this.axiosInstance.post(`/OrderSessions/${sessionId}/call-waiter`);
      console.log(`[customerService] Kellner erfolgreich gerufen für Session ${sessionId}`);
      return response;
    } catch (error) {
      console.error(`[customerService] Fehler beim Rufen des Kellners für Session ${sessionId}:`, error);
      throw error;
    }
  },

  /**
   * Status des Kellner-Rufs abrufen
   * @param {string} sessionId - ID der Session
   * @returns {Promise<Object>} - Status des Kellner-Rufs
   */
  async getWaiterCallStatus(sessionId) {
    console.log(`[customerService] Rufe Kellner-Ruf-Status für Session ${sessionId} ab`);
    
    try {
      const response = await this.axiosInstance.get(`/OrderSessions/${sessionId}/waiter-call-status`);
      console.log(`[customerService] Kellner-Ruf-Status für Session ${sessionId} erfolgreich abgerufen`);
      return response;
    } catch (error) {
      console.error(`[customerService] Fehler beim Abrufen des Kellner-Ruf-Status für Session ${sessionId}:`, error);
      throw error;
    }
  }
} 